import { Field, FormikProps, FieldProps } from 'formik';
import { Trans, t } from '@lingui/macro';

import * as formHelpers from './form';
import FormErrorMessage from './FormErrorMessage';

import styles from './FormFields.module.css';
import EmailStyles from './EmailField.module.css';
import { Input } from '@iwoca/orion';

const EmailField = ({
  form,
}: {
  form: FormikProps<formHelpers.FormValues>;
}) => {
  return (
    <div className={styles.formGroup}>
      <label htmlFor="applicantEmailAddress">
        <Trans>What is your business email address?</Trans>
      </label>
      <div className={`fs-block ${styles.input}`}>
        <Field
          name="applicantEmailAddress"
          validate={() => {
            if (form.values['applicantEmailAddress'] === '') {
              return t`Please enter a valid email address`;
            }
            return form.errors['applicantEmailAddress'];
          }}
        >
          {({ form, field }: FieldProps<any, formHelpers.FormValues>) => (
            <>
              <Input
                data-testid="email-input"
                {...field}
                id="applicantEmailAddress"
                placeholder={t`Enter your business email`}
                type="email"
                className={`fs-block
                                ${
                                  form.errors.applicantEmailAddress &&
                                  form.touched.applicantEmailAddress &&
                                  styles.inputError
                                }`}
                onChange={async (e) => {
                  form.handleChange(e);
                  form.setStatus('EMAIL_VALIDATION_PENDING');
                  await formHelpers.debouncedEmailValidation(form, e);
                }}
                onBlur={async (e) => {
                  form.handleBlur(e);
                  formHelpers.debouncedEmailValidation.flush();
                }}
              />
              {form.status === 'EMAIL_VALIDATED' && (
                <div className={EmailStyles.emailCheck}>
                  <ConfirmedIcon className={EmailStyles.emailCheckIcon} />
                </div>
              )}
            </>
          )}
        </Field>
        <FormErrorMessage name="applicantEmailAddress" />
      </div>
    </div>
  );
};

type Props = {
  className?: string;
};

const ConfirmedIcon: React.FC<Props> = ({ className }) => {
  return (
    <svg
      data-testid="email-success"
      className={className}
      width="34"
      height="34"
      viewBox="0 0 34 34"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <mask
        id="path-1-outside-1"
        maskUnits="userSpaceOnUse"
        x="0"
        y="0"
        width="34"
        height="34"
        fill="black"
      >
        <rect fill="white" width="34" height="34" />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M17 32C25.2843 32 32 25.2843 32 17C32 8.71573 25.2843 2 17 2C8.71573 2 2 8.71573 2 17C2 25.2843 8.71573 32 17 32ZM14.096 19.8664L21.558 12.2896C21.948 11.8936 22.624 11.8936 22.962 12.316C23.378 12.7384 23.378 13.3984 22.962 13.8208L14.798 22.1104C14.616 22.3216 14.33 22.4272 14.07 22.4272C13.81 22.4272 13.55 22.3216 13.342 22.1104L9.312 18.0184C8.896 17.596 8.896 16.936 9.312 16.5136C9.728 16.0912 10.378 16.0912 10.794 16.5136L14.096 19.8664Z"
        />
      </mask>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M17 32C25.2843 32 32 25.2843 32 17C32 8.71573 25.2843 2 17 2C8.71573 2 2 8.71573 2 17C2 25.2843 8.71573 32 17 32ZM14.096 19.8664L21.558 12.2896C21.948 11.8936 22.624 11.8936 22.962 12.316C23.378 12.7384 23.378 13.3984 22.962 13.8208L14.798 22.1104C14.616 22.3216 14.33 22.4272 14.07 22.4272C13.81 22.4272 13.55 22.3216 13.342 22.1104L9.312 18.0184C8.896 17.596 8.896 16.936 9.312 16.5136C9.728 16.0912 10.378 16.0912 10.794 16.5136L14.096 19.8664Z"
        fill="#00B67A"
      />
      <path
        d="M21.558 12.2896L22.983 13.693L22.983 13.693L21.558 12.2896ZM14.096 19.8664L12.671 21.2698L14.096 22.7167L15.521 21.2698L14.096 19.8664ZM22.962 12.316L21.4004 13.5656L21.4648 13.646L21.537 13.7194L22.962 12.316ZM22.962 13.8208L21.537 12.4175L21.537 12.4175L22.962 13.8208ZM14.798 22.1104L13.373 20.7071L13.3264 20.7544L13.2829 20.8048L14.798 22.1104ZM13.342 22.1104L14.767 20.7071L14.767 20.7071L13.342 22.1104ZM9.312 18.0184L10.737 16.6151L10.737 16.6151L9.312 18.0184ZM9.312 16.5136L10.737 17.917L10.737 17.917L9.312 16.5136ZM10.794 16.5136L12.219 15.1103L12.219 15.1103L10.794 16.5136ZM30 17C30 24.1797 24.1797 30 17 30V34C26.3888 34 34 26.3888 34 17H30ZM17 4C24.1797 4 30 9.8203 30 17H34C34 7.61116 26.3888 0 17 0V4ZM4 17C4 9.8203 9.8203 4 17 4V0C7.61116 0 0 7.61116 0 17H4ZM17 30C9.8203 30 4 24.1797 4 17H0C0 26.3888 7.61116 34 17 34V30ZM20.133 10.8863L12.671 18.4631L15.521 21.2698L22.983 13.693L20.133 10.8863ZM24.5236 11.0665C23.3415 9.58923 21.2526 9.74944 20.133 10.8863L22.983 13.693C22.7721 13.9071 22.5038 13.9989 22.2541 13.9958C21.9989 13.9925 21.6546 13.8832 21.4004 13.5656L24.5236 11.0665ZM24.387 15.2242C25.5697 14.0233 25.5697 12.1136 24.387 10.9127L21.537 13.7194C21.1863 13.3633 21.1863 12.7736 21.537 12.4175L24.387 15.2242ZM16.223 23.5138L24.387 15.2242L21.537 12.4175L13.373 20.7071L16.223 23.5138ZM14.07 24.4272C14.8053 24.4272 15.6866 24.143 16.3131 23.416L13.2829 20.8048C13.422 20.6434 13.5742 20.5518 13.6942 20.5018C13.8126 20.4525 13.9376 20.4272 14.07 20.4272V24.4272ZM11.917 23.5138C12.4856 24.0911 13.2528 24.4272 14.07 24.4272V20.4272C14.3672 20.4272 14.6144 20.5521 14.767 20.7071L11.917 23.5138ZM7.88703 19.4218L11.917 23.5138L14.767 20.7071L10.737 16.6151L7.88703 19.4218ZM7.88703 15.1103C6.70432 16.3112 6.70432 18.2209 7.88703 19.4218L10.737 16.6151C11.0877 16.9712 11.0877 17.5609 10.737 17.917L7.88703 15.1103ZM12.219 15.1103C11.0195 13.8924 9.08648 13.8924 7.88703 15.1103L10.737 17.917C10.57 18.0865 10.3236 18.1968 10.053 18.1968C9.78239 18.1968 9.53597 18.0865 9.36903 17.917L12.219 15.1103ZM15.521 18.4631L12.219 15.1103L9.36903 17.917L12.671 21.2698L15.521 18.4631Z"
        fill="white"
        mask="url(#path-1-outside-1)"
      />
    </svg>
  );
};

export default EmailField;
